import { colors as akColors } from '@atlaskit/theme';
import { getTokenValue } from '@atlaskit/tokens';
import type { BurnupChartData, SprintScopeChangeData, SprintEndData } from '../../../common/types';
import { shouldRenderTodayLine } from './get-completed-work-series';

type Series = [string, number][];

const reduceScopeChangeEvents = (series: Series, event: SprintScopeChangeData): Series => {
	// Need to push twice to create the blocky line effect on the graph
	series.push([event.timestamp, event.prevScope]);
	series.push([event.timestamp, event.scope]);
	return series;
};

const getEndEvent = (
	endEvent: SprintEndData | null,
	now: Date,
	maxDate: Date,
	lastVal: number,
): Series => {
	if (endEvent) {
		return [[endEvent.timestamp, endEvent.scope]];
	}
	if (now < maxDate) {
		return [[now.toISOString(), lastVal]];
	}
	return [[maxDate.toISOString(), lastVal]];
};

export const createSeries = (burnup: BurnupChartData, now: Date, maxDate: Date): Series => {
	const { sprintStartEvent, scopeChangeEvents, sprintEndEvent } = burnup;
	const startEvent: Series = [[sprintStartEvent.timestamp, sprintStartEvent.scopeEstimate]]; // types enforcing the non null
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const data: Series = scopeChangeEvents.reduce<Array<any>>(reduceScopeChangeEvents, []);
	// Find the last point in the series
	const previousPoint = data.length ? data[data.length - 1] : startEvent[0];
	// Get the scope from the last point
	const previousValue = previousPoint[1];
	// if there is no sprint end event then we need to send the lastval through to create the line
	const endEvent: Series = getEndEvent(sprintEndEvent, now, maxDate, previousValue);
	return [...startEvent, ...data, ...endEvent];
};

const getScopeChangeLine = (burnup: BurnupChartData, now: Date, maxDate: Date) => {
	const dataTillToday: Series = createSeries(burnup, now, maxDate);
	const prev = dataTillToday.length ? dataTillToday[dataTillToday.length - 1][1] : 0;
	return {
		type: 'line',
		symbol: 'none',
		cursor: 'initial',
		lineStyle: {
			color: getTokenValue('color.chart.danger', akColors.R400),
		},
		markLine: shouldRenderTodayLine(burnup, now, maxDate)
			? {
					lineStyle: {
						color: akColors.R400,
						width: 3,
						type: 'dotted',
					},
					silent: true,
					symbol: 'none',
					data: [
						[
							{
								coord: [now.getTime(), prev],
							},
							{
								coord: [maxDate.getTime(), prev],
							},
						],
					],
				}
			: null,
		data: dataTillToday,
		z: 3,
	};
};

export default getScopeChangeLine;
